// src/components/AddInstructorModal.jsx
import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { addInstructor } from "../services/adminApi";
import { X } from "lucide-react";

export default function AddInstructorModal({
  isOpen,
  onClose,
  hierarchy = { conferences: [], stations: [], districts: [], churches: [] },
  defaultSelection = {},
  onCreated,
}) {
  const { getToken } = useAuth();

  const [email, setEmail] = useState("");
  const [firstName, setFirst] = useState("");
  const [lastName, setLast] = useState("");
  const [phone, setPhone] = useState("");

  const [conferenceId, setConferenceId] = useState(defaultSelection.conferenceId || "all");
  const [stationId, setStationId] = useState(defaultSelection.stationId || "all");
  const [districtId, setDistrictId] = useState(defaultSelection.districtId || "all");
  const [churchId, setChurchId] = useState(defaultSelection.churchId || "all");

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  // 🔁 Sync selection from parent filters whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setConferenceId(defaultSelection.conferenceId || "all");
    setStationId(defaultSelection.stationId || "all");
    setDistrictId(defaultSelection.districtId || "all");
    setChurchId(defaultSelection.churchId || "all");
  }, [isOpen, defaultSelection]);

  const availableStations = useMemo(() => {
    if (conferenceId === "all") return [];
    return (hierarchy.stations || []).filter(
      (s) => String(s.conf_id) === String(conferenceId) && s.isactive === "1"
    );
  }, [conferenceId, hierarchy.stations]);

  const availableDistricts = useMemo(() => {
    if (stationId === "all") return [];
    return (hierarchy.districts || []).filter(
      (d) => String(d.station_id) === String(stationId) && d.isactive === "1"
    );
  }, [stationId, hierarchy.districts]);

  const availableChurches = useMemo(() => {
    if (districtId === "all") return [];
    return (hierarchy.churches || []).filter(
      (c) => String(c.district_id) === String(districtId) && c.isactive === "1"
    );
  }, [districtId, hierarchy.churches]);

  if (!isOpen) return null;

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");

    if (!email.trim()) {
      setMsg("Email is required");
      return;
    }
    if (churchId === "all") {
      setMsg("Select a church for the instructor");
      return;
    }

    try {
      setSubmitting(true);
      const token = await getToken();
      const payload = {
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        conferenceId: conferenceId !== "all" ? conferenceId : undefined,
        stationId: stationId !== "all" ? stationId : undefined,
        districtId: districtId !== "all" ? districtId : undefined,
        churchId, // required
      };

      const res = await addInstructor(token, payload);

      setMsg("✅ Invitation sent and instructor saved!");
      if (onCreated) onCreated({ email, firstName, lastName, churchId, res });

      // Reset form lightly
      setEmail("");
      setFirst("");
      setLast("");
      setPhone("");
      setConferenceId("all");
      setStationId("all");
      setDistrictId("all");
      setChurchId("all");

      setTimeout(() => {
        setMsg("");
        onClose?.();
      }, 900);
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to create instructor");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <h3 className="text-xl font-bold text-gray-900 mb-1">Add Instructor</h3>
        <p className="text-sm text-gray-500 mb-4">
          Invite a new instructor and link them to a church. An email invitation will be sent.
        </p>

        {msg && (
          <div
            className={`mb-4 px-4 py-2 rounded-xl ${
              msg.startsWith("✅") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}
          >
            {msg}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5">
          {/* Identity */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirst(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLast(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+2547XXXXXXX"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
              />
            </div>
          </div>

          {/* Hierarchy */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Conference</label>
              <select
                value={conferenceId}
                onChange={(e) => {
                  setConferenceId(e.target.value);
                  setStationId("all");
                  setDistrictId("all");
                  setChurchId("all");
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl"
              >
                <option value="all">Select conference</option>
                {(hierarchy.conferences || [])
                  .filter((c) => c.isactive !== "0")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Station</label>
              <select
                value={stationId}
                disabled={conferenceId === "all"}
                onChange={(e) => {
                  setStationId(e.target.value);
                  setDistrictId("all");
                  setChurchId("all");
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl disabled:bg-gray-100"
              >
                <option value="all">Select station</option>
                {availableStations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">District</label>
              <select
                value={districtId}
                disabled={stationId === "all"}
                onChange={(e) => {
                  setDistrictId(e.target.value);
                  setChurchId("all");
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl disabled:bg-gray-100"
              >
                <option value="all">Select district</option>
                {availableDistricts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Church *</label>
              <select
                value={churchId}
                disabled={districtId === "all"}
                onChange={(e) => setChurchId(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl disabled:bg-gray-100"
                required
              >
                <option value="all">Select church</option>
                {availableChurches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 text-white font-semibold hover:from-orange-700 hover:to-amber-700 disabled:opacity-60"
            >
              {submitting ? "Inviting…" : "Invite Instructor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
